import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const BlogPreview = () => {
    const { t } = useLanguage();
    const B = t.blog;
    const posts = (B.posts || []).slice(0, 3);

    return (
        <section
            id="blog"
            data-testid="blog-preview-section"
            className="py-24 md:py-32 bg-ella-onyx text-ella-cream relative overflow-hidden"
        >
            <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
                <div className="absolute top-1/4 -left-20 w-96 h-96 rounded-full bg-ella-gold blur-3xl" />
                <div className="absolute bottom-0 -right-20 w-96 h-96 rounded-full bg-ella-gold blur-3xl" />
            </div>

            <div className="ella-container relative">
                <div className="max-w-3xl">
                    <span className="ella-overline-light" data-testid="blog-preview-overline">
                        {B.overline}
                    </span>
                    <h2
                        className="ella-headline text-4xl md:text-5xl lg:text-6xl mt-4 text-ella-cream"
                        data-testid="blog-preview-title"
                    >
                        {B.title}
                    </h2>
                    <p className="mt-6 text-ella-cream/70 font-light leading-relaxed text-lg">
                        {B.subtitle}
                    </p>
                </div>

                <div className="mt-16 grid md:grid-cols-3 gap-8" data-testid="blog-preview-grid">
                    {posts.map((post, idx) => (
                        <Link
                            key={post.slug}
                            to={`/journal/${post.slug}`}
                            data-testid={`blog-card-${post.slug}`}
                            className="group flex flex-col bg-white/[0.03] border border-white/10 rounded-3xl overflow-hidden hover:bg-white/[0.06] hover:border-ella-gold/40 transition-all duration-500"
                        >
                            <div className="relative aspect-[16/10] overflow-hidden">
                                <img
                                    src={post.image}
                                    alt={post.title}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div className="absolute top-4 left-4 px-3 py-1 bg-ella-cream/95 text-ella-ink text-[10px] uppercase tracking-[0.24em] rounded-full">
                                    {post.category}
                                </div>
                            </div>
                            <div className="flex flex-col flex-1 p-7">
                                <div className="flex items-center gap-3 text-[11px] text-ella-cream/50 uppercase tracking-[0.2em]">
                                    <span>{post.date}</span>
                                    <span className="text-ella-gold/60">·</span>
                                    <span className="inline-flex items-center gap-1">
                                        <Clock className="w-3 h-3" strokeWidth={1.5} />
                                        {post.read_minutes} {B.min_read}
                                    </span>
                                </div>
                                <h3 className="font-display text-2xl md:text-[26px] mt-4 text-ella-cream leading-tight group-hover:text-ella-gold transition-colors">
                                    {post.title}
                                </h3>
                                <p className="mt-4 text-ella-cream/65 font-light leading-relaxed text-[15px] flex-1">
                                    {post.excerpt}
                                </p>
                                <div className="mt-6 inline-flex items-center gap-2 text-ella-gold text-xs uppercase tracking-[0.24em]">
                                    {B.cta_read}
                                    <ArrowRight
                                        className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform"
                                        strokeWidth={1.5}
                                    />
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>

                <div className="mt-16 text-center">
                    <Link
                        to="/journal"
                        data-testid="blog-preview-cta-all"
                        className="inline-flex items-center gap-3 px-9 py-4 rounded-full border border-ella-gold/60 text-ella-gold hover:bg-ella-gold hover:text-ella-onyx transition-all text-xs uppercase tracking-[0.24em]"
                    >
                        {B.cta_all}
                        <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default BlogPreview;
